import { useMemo, useState } from 'react';
import type { Generator } from '../../hooks/useGenerators';
import type { Organization } from '../../hooks/useCurrentOrg';
import { buildPublicGeneratorUrl, copyToClipboard } from '../../lib/publicUrl';
import {
    buildIframeSnippet,
    buildLinkSnippet,
    buildWidgetSnippet,
} from '../../lib/embedSnippets';
import { Icon } from '../Icon';

interface IntegrationPanelProps {
    generator: Generator;
    org: Organization;
}

type TabId = 'link' | 'iframe' | 'widget';

const TABS: { id: TabId; label: string; icon: 'link' | 'code' | 'globe' }[] = [
    { id: 'link', label: 'Lien', icon: 'link' },
    { id: 'iframe', label: 'Iframe', icon: 'code' },
    { id: 'widget', label: 'Widget', icon: 'globe' },
];

/**
 * Panneau « Intégrer ce générateur » — propose trois modes d'intégration
 * pour les sites tiers : lien direct, iframe auto-resize, et widget JS.
 */
export function IntegrationPanel({ generator, org }: IntegrationPanelProps) {
    const [tab, setTab] = useState<TabId>('iframe');
    const [widgetMode, setWidgetMode] = useState<'inline' | 'popup'>('inline');
    const [copied, setCopied] = useState(false);

    const publicUrl = useMemo(
        () => buildPublicGeneratorUrl(org.slug, generator.slug),
        [org.slug, generator.slug],
    );

    const snippet = useMemo(() => {
        const opts = {
            publicUrl,
            mode: widgetMode,
        } as const;
        if (tab === 'link') return buildLinkSnippet(opts);
        if (tab === 'iframe') return buildIframeSnippet(opts);
        return buildWidgetSnippet(opts);
    }, [tab, widgetMode, publicUrl]);

    const handleCopy = async () => {
        const ok = await copyToClipboard(snippet);
        if (ok) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        }
    };

    const notPublished = !generator.is_published;

    return (
        <section className="integ-panel">
            <header className="integ-head">
                <div>
                    <h3 className="integ-title">Intégrer ce générateur</h3>
                    <p className="integ-sub">
                        Ajoutez votre générateur à un site existant en quelques secondes.
                    </p>
                </div>
            </header>

            {notPublished && (
                <div className="integ-warn">
                    Publiez votre générateur avant de l'intégrer sur un site tiers.
                </div>
            )}

            <nav className="integ-tabs" role="tablist" aria-label="Type d'intégration">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        className={`integ-tab${tab === t.id ? ' is-active' : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        <Icon name={t.icon} size={14} />
                        <span>{t.label}</span>
                    </button>
                ))}
            </nav>

            <div className="integ-body">
                {tab === 'link' && (
                    <p className="integ-hint">
                        Ajoutez simplement un bouton ou un lien qui ouvre votre générateur
                        public. Idéal quand vous ne pouvez pas modifier le HTML de votre
                        site en profondeur.
                    </p>
                )}
                {tab === 'iframe' && (
                    <p className="integ-hint">
                        Intègre le générateur directement dans votre page. L'iframe
                        s'adapte automatiquement à sa hauteur. Compatible avec la plupart
                        des CMS (WordPress, Webflow, Wix, Squarespace…).
                    </p>
                )}
                {tab === 'widget' && (
                    <>
                        <p className="integ-hint">
                            Script léger qui crée l'iframe pour vous et gère le
                            redimensionnement. Deux variantes :
                        </p>
                        <div className="integ-mode-switch" role="radiogroup" aria-label="Mode d'affichage">
                            <button
                                type="button"
                                role="radio"
                                aria-checked={widgetMode === 'inline'}
                                className={`integ-mode${widgetMode === 'inline' ? ' is-active' : ''}`}
                                onClick={() => setWidgetMode('inline')}
                            >
                                <strong>Inline</strong>
                                <span>Dans un conteneur de votre page</span>
                            </button>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={widgetMode === 'popup'}
                                className={`integ-mode${widgetMode === 'popup' ? ' is-active' : ''}`}
                                onClick={() => setWidgetMode('popup')}
                            >
                                <strong>Bouton flottant</strong>
                                <span>Ouvre le générateur en pop-up</span>
                            </button>
                        </div>
                    </>
                )}

                <div className="integ-snippet">
                    <div className="integ-snippet-head">
                        <span>Code à copier-coller</span>
                        <button
                            type="button"
                            className="integ-copy"
                            onClick={handleCopy}
                            disabled={notPublished}
                        >
                            <Icon name={copied ? 'check' : 'copy'} size={13} />
                            {copied ? 'Copié' : 'Copier'}
                        </button>
                    </div>
                    <pre className="integ-code" aria-label="Code d'intégration">
                        <code>{snippet}</code>
                    </pre>
                </div>

                <div className="integ-public-url">
                    <span className="integ-public-label">URL publique</span>
                    <code className="integ-public-value">{publicUrl}</code>
                </div>
            </div>
        </section>
    );
}
